import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

const KEY_RE = /^[a-z0-9]+(_[a-z0-9]+)*$/;

function CreateFlag({ open, onOpenChange, onCreated }) {
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const valid = KEY_RE.test(key);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api("/admin/flags", { body: { key, description: description.trim(), enabled: false } });
      onCreated(res.flag);
      onOpenChange(false);
      setKey(""); setDescription("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[20px]" data-testid="flag-create-dialog">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader><DialogTitle>New feature flag</DialogTitle><DialogDescription>New flags start Off. The app reads them from /api/flags.</DialogDescription></DialogHeader>
          <div><Label htmlFor="flag-key">Key</Label>
            <Input id="flag-key" value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[\s-]/g, "_"))} placeholder="new_chat_ui" className="mt-2 font-mono" aria-invalid={!!key && !valid} data-testid="flag-key-input" />
            <p className={`mt-2 text-xs ${key && !valid ? "cw-error" : "cw-muted"}`}>snake_case: lowercase letters, numbers and underscores.</p></div>
          <div><Label htmlFor="flag-desc">Description</Label>
            <Textarea id="flag-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this flag turns on" className="mt-2" data-testid="flag-description-input" /></div>
          {error && <p className="cw-error text-sm" role="alert" data-testid="flag-create-error">{error}</p>}
          <DialogFooter><Button type="submit" disabled={busy || !valid} data-testid="flag-create-submit-button">{busy ? "Creating…" : "Create flag"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Flags() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/flags");
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");

  const toggle = async () => {
    const r = reason.trim();
    if (r.length < 3 || r.length > 500) throw new Error("The reason must be 3–500 characters.");
    const { f, enabled } = pending;
    const res = await api(`/admin/flags/${f.key}`, { method: "PATCH", body: { enabled, reason: r } });
    setData((d) => ({ items: d.items.map((x) => (x.key === f.key ? { ...x, ...res.flag } : x)) }));
    toast.success(`${f.key} is ${enabled ? "on" : "off"}.`);
  };
  const del = async () => {
    await api(`/admin/flags/${deleting.key}`, { method: "DELETE" });
    setData((d) => ({ items: d.items.filter((x) => x.key !== deleting.key) }));
    toast.success("Flag deleted.");
  };

  return (
    <div data-testid="admin-flags-page">
      <PageHeader title="Feature flags" description="The feature registry. Each switch applies to everyone right away and needs a reason."
        actions={<Button onClick={() => setCreating(true)} data-testid="flag-create-open-button"><Plus />New flag</Button>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          {data?.items?.length ? <div className="overflow-x-auto"><table className="w-full min-w-[820px] text-left" data-testid="flags-table">
            <thead><tr className="cw-muted border-b border-border text-xs"><th className="py-3 pl-5 font-medium">Key</th><th className="py-3 pr-4 font-medium">What it does</th><th className="py-3 pr-4 font-medium">Introduced</th><th className="py-3 pr-4 font-medium">Enabled</th><th className="w-14" /></tr></thead>
            <tbody>{data.items.map((f) => (
              <tr key={f.key} className="border-b border-border align-top last:border-0" data-testid={`flag-row-${f.key}`}>
                <td className="py-4 pl-5 pr-4"><label htmlFor={`flag-${f.key}`} className="font-mono text-sm font-medium">{f.key}</label>
                  {f.registered === false && <Pill tone="warning" className="mt-1 block w-fit">Not in registry</Pill>}</td>
                <td className="py-4 pr-4"><p className="cw-soft max-w-md text-sm">{f.description || "—"}</p><p className="cw-muted mt-1 text-xs">Updated {formatDate(f.updated_at)}{f.updated_by ? ` by ${f.updated_by}` : ""}</p>
                  {f.last_reason && <p className="cw-soft mt-1 text-xs" data-testid={`flag-last-reason-${f.key}`}>Last reason: “{f.last_reason}”</p>}</td>
                <td className="cw-soft whitespace-nowrap py-4 pr-4 text-sm" data-testid={`flag-introduced-${f.key}`}>{f.introduced_in != null ? `Prompt ${f.introduced_in}` : "—"}</td>
                <td className="py-4 pr-4"><div className="flex items-center gap-3">
                  <Switch id={`flag-${f.key}`} checked={!!f.enabled} onCheckedChange={(v) => { setReason(""); setPending({ f, enabled: v }); }} data-testid={`flag-toggle-${f.key}`} />
                  <span className={`text-xs font-medium ${f.enabled ? "cw-brand-deep" : "cw-muted"}`} data-testid={`flag-state-${f.key}`}>{f.enabled ? "On" : "Off"}</span></div></td>
                <td className="py-3 pr-3">{f.registered === false && <Button size="icon" variant="ghost" className="cw-error" aria-label={`Delete ${f.key}`} onClick={() => setDeleting(f)} data-testid={`flag-delete-button-${f.key}`}><Trash /></Button>}</td>
              </tr>
            ))}</tbody>
          </table></div> : <Empty title="No flags yet" testId="flags-empty" />}
        </Panel>
      </LoadState>
      <CreateFlag open={creating} onOpenChange={setCreating} onCreated={(flag) => { setData((d) => ({ items: [...(d?.items || []), flag].sort((a, b) => a.key.localeCompare(b.key)) })); toast.success("Flag created."); }} />
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} testId="flag-toggle-confirm-dialog" onConfirm={toggle} confirmDisabled={reason.trim().length < 3 || reason.trim().length > 500}
        destructive={pending ? !pending.enabled : false} confirmLabel={pending?.enabled ? "Turn on" : "Turn off"}
        title={`Turn ${pending?.f.key} ${pending?.enabled ? "on" : "off"}?`} description="It applies for everyone right away. The change is recorded in the Admin Log.">
        <div><Label htmlFor="flag-reason">Reason</Label>
          <Textarea id="flag-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2" data-testid="flag-reason-input" />
          <p className="cw-muted mt-1 text-xs">{reason.trim().length}/500 · at least 3 characters. Saved on the flag and in the Admin Log.</p></div>
      </ConfirmDialog>
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testId="flag-delete-confirm-dialog"
        title={`Delete ${deleting?.key}?`} description="The app will treat this flag as Off." confirmLabel="Delete flag" onConfirm={del} />
    </div>
  );
}
